import React, { createContext, useContext, useState, useMemo } from 'react';
import {
  node,
  string,
  bool,
  shape,
  arrayOf,
  oneOf
} from 'prop-types';
import { Skeleton, SkeletonLine } from '@one-thd/sui-atomic-components';
import { useApplianceFuelType } from '../hooks/useApplianceFuelType';
import { track } from '../utils';
import { ENTRY_TYPES } from '../constants';

const FuelTypeContext = createContext({
  children: null,
  itemId: '',
  isCaching: false
});
export const useFuelType = () => useContext(FuelTypeContext);

export const FuelTypeProvider = ({
  children,
  itemId,
  entryType,
  position,
  isCaching,
  product,
  metadata,
  search
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const onAlertClick = () => {
    setDrawerOpen(true);
    track({ eventType: 'click' });
  };

  const {
    types,
    categories,
    isDryer,
    isRange,
    isEither,
    hasDualFuel,
    loading,
    error
  } = useApplianceFuelType({
    itemId,
    product,
    metadata,
    search
  });

  const value = useMemo(() => ({
    entryType,
    position,
    onAlertClick,
    drawerOpen,
    setDrawerOpen,
    types,
    categories,
    isDryer,
    isRange,
    isEither,
    hasDualFuel
  }), [
    entryType,
    position,
    onAlertClick,
    drawerOpen,
    setDrawerOpen,
    types,
    categories,
    isDryer,
    isRange,
    isEither,
    hasDualFuel
  ]);

  if (isCaching || loading) {
    return (
      <Skeleton
        data-testid="appliance-fuel-type-loader"
        grow
        disablePadding
      >
        <SkeletonLine variant="single" />
      </Skeleton>
    );
  }

  if (!(loading || !error)
    || !categories?.length
    || !ENTRY_TYPES.includes(entryType)) {
    return null;
  }

  return (
    <FuelTypeContext.Provider value={value}>
      {children}
    </FuelTypeContext.Provider>
  );
};

FuelTypeProvider.propTypes = {
  children: node.isRequired,
  itemId: string,
  entryType: oneOf(ENTRY_TYPES).isRequired,
  position: oneOf(['left', 'right']).isRequired,
  product: shape({
    itemId: string,
    dataSource: string,
    specificationGroup: arrayOf(shape({
      specTitle: string,
      specifications: arrayOf(shape({
        specName: string,
        specValue: string
      }))
    }))
  }),
  metadata: shape({
    attributes: arrayOf(shape({
      attributeValues: arrayOf(shape({
        value: string
      })),
      attributeName: string
    })),
    superDuperSku: shape({
      attributes: arrayOf(shape({
        attributeName: string,
        attributeValues: arrayOf(shape({
          selected: bool,
          value: string
        }))
      }))
    })
  }),
  search: shape({
    dimensions: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    })),
    primaryFilters: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    }))
  }),
  isCaching: bool.isRequired
};

FuelTypeProvider.defaultProps = {
  itemId: '',
  product: null,
  metadata: null,
  search: null
};
